import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, map, of, switchMap, tap, throwError } from 'rxjs';

import { CurrentUser, LoginRequest, LoginResponse } from '../models/auth.model';
import { ApiClientService } from './api-client.service';
import { TokenStorageService } from './token-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiClient = inject(ApiClientService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly currentUserSignal = signal<CurrentUser | null>(null);
  private readonly restoringSessionSignal = signal(false);

  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isRestoringSession = this.restoringSessionSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.currentUserSignal() !== null);

  login(request: LoginRequest): Observable<CurrentUser> {
    return this.apiClient.postRaw<LoginResponse, LoginRequest>('/v1/auth/login', request).pipe(
      tap(response => this.tokenStorage.setToken(response.access_token)),
      switchMap(() => this.loadCurrentUser()),
      map(user => {
        if (user === null) {
          throw new Error('Unable to restore authenticated user.');
        }

        return user;
      }),
      catchError(error => {
        this.clearSession();
        return throwError(() => error);
      })
    );
  }

  logout(): Observable<void> {
    if (!this.getToken()) {
      this.clearSession();
      return of(undefined);
    }

    return this.apiClient.postRaw<unknown, Record<string, never>>('/v1/auth/logout', {}).pipe(
      catchError(() => of(undefined)),
      tap(() => this.clearSession()),
      map(() => undefined)
    );
  }

  loadCurrentUser(): Observable<CurrentUser | null> {
    if (!this.getToken()) {
      this.clearSession();
      return of(null);
    }

    this.restoringSessionSignal.set(true);

    return this.apiClient.getRaw<CurrentUser>('/v1/auth/me').pipe(
      tap(user => this.currentUserSignal.set(user)),
      map(user => user),
      catchError(error => {
        this.clearSession();
        return throwError(() => error);
      }),
      finalize(() => this.restoringSessionSignal.set(false))
    );
  }

  restoreSession(): Observable<CurrentUser | null> {
    if (this.currentUserSignal()) {
      return of(this.currentUserSignal());
    }

    if (!this.getToken()) {
      return of(null);
    }

    return this.loadCurrentUser().pipe(catchError(() => of(null)));
  }

  getCurrentUser(): CurrentUser | null {
    return this.currentUserSignal();
  }

  getToken(): string | null {
    return this.tokenStorage.getToken();
  }

  hasStoredToken(): boolean {
    return this.getToken() !== null;
  }

  clearSession(): void {
    this.tokenStorage.clearToken();
    this.currentUserSignal.set(null);
    this.restoringSessionSignal.set(false);
  }
}
